/**
 * Renderer-agnostic presentation state for Planogram entities.
 *
 * The resolver intentionally accepts only stable semantic IDs. Consumers may
 * render the returned states in any presentation mode without changing scene
 * geometry, identity, or hit-test behavior.
 */

export type OverlayChannelMode = "de-emphasis" | "highlight";

export interface OverlaySelectionInput {
  readonly ids: readonly string[];
  readonly focusedId: string | null;
}

export interface OverlayChannelInput {
  /** A caller-defined channel name, such as search or business. */
  readonly name: string;
  readonly ids: readonly string[];
  readonly mode: OverlayChannelMode;
  /** Higher values win. Equal values are resolved by name, then mode. */
  readonly priority?: number;
  /** Optional semantic pulse state for a channel, without coupling the engine to a domain. */
  readonly pulse?: boolean;
  /**
   * Optional per-item colors (entity id → color). An id without an entry
   * falls back to the renderer's channel color.
   */
  readonly itemColors?: ReadonlyMap<string, string>;
}

export interface OverlayResolutionInput {
  /** The authoritative entity identity and output order. Duplicate IDs are ignored. */
  readonly entityIds: readonly string[];
  readonly selection?: OverlaySelectionInput;
  readonly hoveredId?: string | null;
  readonly channels?: readonly OverlayChannelInput[];
  /**
   * Short per-entity labels (entity id → text), such as numbered violation
   * pins. Badges are their OWN output dimension, exactly like selection and
   * hover: a channel winning emphasis can never erase an entity's badge, and
   * a badge never grants emphasis. Stale ids are ignored.
   */
  readonly badges?: ReadonlyMap<string, string>;
}

export type OverlaySelectionState = "selected" | "unselected";
export type OverlayFocusState = "focused" | "unfocused";
export type OverlayHoverState = "hovered" | "not-hovered";
export type OverlayEmphasisState = "de-emphasized" | "highlighted" | "none";

export interface OverlayEmphasis {
  readonly channel: string | null;
  readonly state: OverlayEmphasisState;
  /** The winning channel's per-item color, when the channel supplied one. */
  readonly color?: string;
}

export type OverlayPresentationToken =
  | { readonly kind: "badge" }
  | { readonly kind: "de-emphasis"; readonly channel: string }
  | { readonly kind: "focus" }
  | { readonly kind: "highlight"; readonly channel: string }
  | { readonly kind: "hover" }
  | { readonly kind: "selection" }
  | { readonly channel: string; readonly kind: "pulse" };

export interface OverlayPresentation {
  readonly id: string;
  readonly selection: OverlaySelectionState;
  readonly focus: OverlayFocusState;
  readonly hover: OverlayHoverState;
  readonly emphasis: OverlayEmphasis;
  /** Short label pinned to the entity, or null when it carries none. */
  readonly badge: string | null;
  /** Stable semantic tokens for renderers and accessibility adapters. */
  readonly tokens: readonly OverlayPresentationToken[];
}

interface EffectiveChannel {
  readonly color?: string;
  readonly mode: OverlayChannelMode;
  readonly name: string;
  readonly priority: number;
  readonly pulse: boolean;
}

const getPriority = (channel: OverlayChannelInput): number =>
  Number.isFinite(channel.priority) ? (channel.priority ?? 0) : 0;

const compareChannels = (first: EffectiveChannel, second: EffectiveChannel): number => {
  if (first.priority !== second.priority) return second.priority - first.priority;
  if (first.name < second.name) return -1;
  if (first.name > second.name) return 1;
  if (first.mode === second.mode) return 0;
  return first.mode === "highlight" ? -1 : 1;
};

const isBetterChannel = (
  candidate: EffectiveChannel,
  current: EffectiveChannel | undefined,
): boolean => current === undefined || compareChannels(candidate, current) < 0;

const addChannelMatches = (
  matches: Map<string, EffectiveChannel>,
  entityIds: ReadonlySet<string>,
  channel: OverlayChannelInput,
): void => {
  const baseChannel: EffectiveChannel = {
    mode: channel.mode,
    name: channel.name,
    priority: getPriority(channel),
    pulse: channel.pulse === true,
  };

  for (const id of new Set(channel.ids)) {
    if (!entityIds.has(id)) continue;
    const color = channel.itemColors?.get(id);
    const effectiveChannel = color === undefined ? baseChannel : { ...baseChannel, color };
    const current = matches.get(id);
    if (isBetterChannel(effectiveChannel, current)) {
      matches.set(id, effectiveChannel);
    }
  }
};

const createTokens = (
  selection: OverlaySelectionState,
  focus: OverlayFocusState,
  hover: OverlayHoverState,
  emphasis: OverlayEmphasis,
  pulse: boolean,
  badge: string | null,
): readonly OverlayPresentationToken[] => {
  const tokens: OverlayPresentationToken[] = [];

  if (badge !== null) tokens.push({ kind: "badge" });
  if (selection === "selected") tokens.push({ kind: "selection" });
  if (focus === "focused") tokens.push({ kind: "focus" });
  if (hover === "hovered") tokens.push({ kind: "hover" });
  if (emphasis.state === "highlighted" && emphasis.channel !== null) {
    tokens.push({ channel: emphasis.channel, kind: "highlight" });
  }
  if (emphasis.state === "de-emphasized" && emphasis.channel !== null) {
    tokens.push({ channel: emphasis.channel, kind: "de-emphasis" });
  }
  if (pulse && emphasis.channel !== null) {
    tokens.push({ channel: emphasis.channel, kind: "pulse" });
  }

  return tokens;
};

/**
 * Resolves independent interaction state and externally supplied overlays.
 *
 * Selection, focus, hover, and badges are separate output dimensions. A channel may
 * win only the emphasis dimension, so business/search/group adapters cannot
 * erase selection or focus. Channel priority is descending; equal-priority
 * channels use lexical name order and then highlight over de-emphasis. Stale
 * and duplicate IDs are ignored.
 */
export const resolveOverlayPresentation = (
  input: OverlayResolutionInput,
): readonly OverlayPresentation[] => {
  const entityIds = [...new Set(input.entityIds)];
  const knownEntityIds = new Set(entityIds);
  const selectedIds = new Set(input.selection?.ids ?? []);
  const focusedId = input.selection?.focusedId ?? null;
  const hoveredId = input.hoveredId ?? null;
  const channelMatches = new Map<string, EffectiveChannel>();

  for (const channel of input.channels ?? []) {
    addChannelMatches(channelMatches, knownEntityIds, channel);
  }

  return entityIds.map((id) => {
    const selection: OverlaySelectionState = selectedIds.has(id) ? "selected" : "unselected";
    const focus: OverlayFocusState = focusedId === id ? "focused" : "unfocused";
    const hover: OverlayHoverState = hoveredId === id ? "hovered" : "not-hovered";
    const channel = channelMatches.get(id);
    const emphasis: OverlayEmphasis = channel
      ? {
          channel: channel.name,
          state: channel.mode === "highlight" ? "highlighted" : "de-emphasized",
          ...(channel.color === undefined ? {} : { color: channel.color }),
        }
      : { channel: null, state: "none" };

    const badge = input.badges?.get(id) ?? null;

    return {
      badge,
      emphasis,
      focus,
      hover,
      id,
      selection,
      tokens: createTokens(selection, focus, hover, emphasis, channel?.pulse ?? false, badge),
    };
  });
};
